/**
 * MealEditorModal - Edit meals & treats for a subscription with plus/minus buttons
 */

import React, { useState, useMemo, useEffect } from "react";
import {
  Alert,
  Box,
  Dialog,
  IconButton,
  Typography,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";

import { QuizButton } from "../shared/QuizButton";
import { buildVariantIndex } from "../../utils/recharge";
import type { BundlePlan, LegacySubscription } from "../../utils/recharge";
import { filterRecipesByAllergies } from "@/helpers/mealDistribution";
import { QUIZ } from "@/theme/quizTokens";

type Props = {
  open: boolean;
  onClose: () => void;
  plan: BundlePlan | LegacySubscription | null;
  products: any[];
  onAddToBasket: (changes: MealChange[]) => void;
};

export type MealChange = {
  variantId: string;
  productId: string;
  productTitle: string;
  variantTitle: string;
  imageUrl: string | null;
  quantity: number; // positive = add, negative = remove
  price: number;
};

type RecipeItem = {
  variantId: string;
  productId: string;
  productTitle: string;
  variantTitle: string;
  imageUrl: string | null;
  price: number;
  currentCount: number; // existing in subscription
};

export function MealEditorModal({
  open,
  onClose,
  plan,
  products,
  onAddToBasket,
}: Props) {
  const [selection, setSelection] = useState<Record<string, number>>({});

  // Build available recipes from products
  const availableRecipes = useMemo(() => {
    if (!plan || !products.length) return [];

    const variantIndex = buildVariantIndex(products);
    const recipes: RecipeItem[] = [];

    // Build a map of current counts from the plan
    const currentCounts = new Map<string, number>();

    if (plan.kind === "bundle" && plan.meals) {
      // Bundle plan - use meals array
      for (const meal of plan.meals) {
        currentCounts.set(meal.variantId, meal.count);
      }
    } else if (plan.kind === "legacy" && plan.variantId) {
      // Legacy subscription - single product with quantity
      currentCounts.set(plan.variantId, plan.quantity);
    }

    // Get all meal products (tagged as "Meals")
    for (const p of products) {
      const tags = Array.isArray(p.tags) ? p.tags : String(p.tags || "").split(",").map((t: any) => t.trim());
      const isMeal = tags.some((t: any) => t.toLowerCase() === "meals");

      if (!isMeal) continue;

      // Filter variants by pack size if available
      for (const v of p.variants || []) {
        const variantId = String(v.id);
        const variantInfo = variantIndex.get(variantId);

        if (!variantInfo) continue;

        // Match pack size if we know it (bundle plans only)
        if (plan.kind === "bundle" && plan.packSizeGrams) {
          const gramsFromTitle = parseInt(v.title, 10);
          if (gramsFromTitle !== plan.packSizeGrams) continue;
        }

        recipes.push({
          variantId,
          productId: variantInfo.productId,
          productTitle: variantInfo.productTitle,
          variantTitle: variantInfo.variantTitle,
          imageUrl: variantInfo.imageUrl,
          price: Number(v.price) || 0,
          currentCount: currentCounts.get(variantId) || 0,
        });
      }
    }

    // Allergy filtering (same rules as the quiz: title keywords, synonyms,
    // hidden allergens like Sammy's & Tilly's→chicken). Recipes already in
    // the subscription stay visible so the customer can remove them.
    const allergies = plan?.dogAllergies ?? [];
    return recipes.filter(
      (r) =>
        r.currentCount > 0 ||
        filterRecipesByAllergies([{ title: r.productTitle }], allergies).length > 0
    );
  }, [plan, products]);

  // Recipes currently in the subscription that conflict with the dog's
  // allergies — kept visible so the customer can remove them, but flagged.
  const conflictedCurrent = useMemo(() => {
    const allergies = plan?.dogAllergies ?? [];
    if (!allergies.length) return [];
    return availableRecipes.filter(
      (r) =>
        r.currentCount > 0 &&
        filterRecipesByAllergies([{ title: r.productTitle }], allergies).length === 0
    );
  }, [availableRecipes, plan]);

  // Initialize selection from current plan
  useEffect(() => {
    if (!plan || !open) return;

    const initial: Record<string, number> = {};

    if (plan.kind === "bundle" && plan.meals) {
      // Bundle plan - set counts from meals array
      for (const meal of plan.meals) {
        initial[meal.variantId] = meal.count;
      }
    } else if (plan.kind === "legacy" && plan.variantId) {
      // Legacy subscription - single product
      initial[plan.variantId] = plan.quantity;
    }

    setSelection(initial);
  }, [plan, open]);

  // Calculate changes
  const changes = useMemo(() => {
    const result: MealChange[] = [];

    for (const recipe of availableRecipes) {
      const current = recipe.currentCount;
      const selected = selection[recipe.variantId] || 0;
      const diff = selected - current;

      if (diff !== 0) {
        result.push({
          variantId: recipe.variantId,
          productId: recipe.productId,
          productTitle: recipe.productTitle,
          variantTitle: recipe.variantTitle,
          imageUrl: recipe.imageUrl,
          quantity: diff,
          price: recipe.price,
        });
      }
    }

    return result;
  }, [availableRecipes, selection]);

  const hasChanges = changes.length > 0;
  const totalAdded = changes.reduce((sum, c) => sum + (c.quantity > 0 ? c.quantity : 0), 0);
  const totalRemoved = changes.reduce((sum, c) => sum + (c.quantity < 0 ? Math.abs(c.quantity) : 0), 0);

  const increment = (variantId: string) => {
    setSelection((prev) => ({
      ...prev,
      [variantId]: (prev[variantId] || 0) + 1,
    }));
  };

  const decrement = (variantId: string) => {
    setSelection((prev) => {
      const current = prev[variantId] || 0;
      if (current <= 0) return prev;
      return { ...prev, [variantId]: current - 1 };
    });
  };

  const handleAddToBasket = () => {
    onAddToBasket(changes);
    onClose();
  };

  if (!plan) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: "16px" },
          maxHeight: { xs: "100%", sm: "90vh" },
          height: { xs: "100%", sm: "auto" },
          m: { xs: 0, sm: 2 },
          width: { xs: "100%", sm: "auto" },
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header - Fixed */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #FAF5EB 0%, #FFF 100%)",
          borderBottom: "1px solid rgba(0,0,0,.08)",
          p: { xs: 2, md: 3 },
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography
              sx={{
                fontSize: { xs: 20, md: 24 },
                fontWeight: 800,
                color: QUIZ.color.brown,
              }}
            >
              Edit Meals & Treats
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 13, md: 14 },
                color: QUIZ.color.brown,
                opacity: 0.65,
                mt: 0.5,
              }}
            >
              {plan.productTitle} • {"packSizeGrams" in plan ? `${plan.packSizeGrams}g pouches` : plan.variantTitle}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: QUIZ.color.brown }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ flex: 1, overflow: "auto", p: { xs: 2, md: 3 } }}>
        {conflictedCurrent.length > 0 && (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: "12px" }}>
            Based on the latest allergy info, these meals in the current plan
            should be replaced:{" "}
            <strong>{conflictedCurrent.map((r) => r.productTitle).join(", ")}</strong>.
            Reduce them to 0 and add safe recipes to keep the same total.
          </Alert>
        )}

        {/* Summary Banner */}
        {hasChanges && (
          <Box
            sx={{
              background: "#E6F0FF",
              border: "2px solid #1A57D3",
              borderRadius: "12px",
              p: 2,
              mb: 3,
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: 14, md: 15 },
                color: "#1A57D3",
                textAlign: "center",
              }}
            >
              📦 {totalAdded > 0 && `Adding ${totalAdded} pots`}
              {totalAdded > 0 && totalRemoved > 0 && " • "}
              {totalRemoved > 0 && `Removing ${totalRemoved} pots`}
            </Typography>
          </Box>
        )}

        {/* Recipe Grid */}
        <Stack spacing={2}>
          {availableRecipes.length === 0 ? (
            <Typography sx={{ textAlign: "center", py: 4, color: QUIZ.color.brown, opacity: 0.65 }}>
              No meals available for this pack size
            </Typography>
          ) : (
            availableRecipes.map((recipe) => {
              const count = selection[recipe.variantId] || 0;
              const current = recipe.currentCount;
              const isChanged = count !== current;

              return (
                <Box
                  key={recipe.variantId}
                  sx={{
                    background: isChanged ? "#FFF9E5" : QUIZ.color.white,
                    borderRadius: "14px",
                    padding: "12px",
                    border: isChanged ? "2px solid #FFE8A3" : "1px solid rgba(0,0,0,0.08)",
                    boxShadow: "0px 2px 6px rgba(0,0,0,0.06)",
                    transition: "0.2s ease",
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      alignItems: "center",
                      gap: 1.5,
                      gridTemplateColumns: {
                        xs: "34px 56px 1fr 34px",
                        sm: "40px 70px 1fr 40px",
                      },
                    }}
                  >
                    {/* Decrement */}
                    <IconButton
                      onClick={() => decrement(recipe.variantId)}
                      disabled={count <= 0}
                      sx={{
                        width: { xs: 30, sm: 34 },
                        height: { xs: 30, sm: 34 },
                        borderRadius: "8px",
                        background: "#FFF4ED",
                        border: "1px solid #FFD8CC",
                        "&:hover": { background: "#FFEADF" },
                        "&:disabled": { opacity: 0.3 },
                      }}
                    >
                      <RemoveRoundedIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: QUIZ.color.brown }} />
                    </IconButton>

                    {/* Image */}
                    <Box
                      sx={{
                        width: { xs: 56, sm: 70 },
                        height: { xs: 56, sm: 70 },
                        borderRadius: "10px",
                        overflow: "hidden",
                        background: QUIZ.color.cream,
                        border: "1px solid rgba(0,0,0,.10)",
                      }}
                    >
                      {recipe.imageUrl ? (
                        <Box
                          component="img"
                          src={recipe.imageUrl}
                          alt={recipe.productTitle}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : null}
                    </Box>

                    {/* Title + grams */}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontFamily: QUIZ.font.display,
                          fontSize: { xs: "0.9rem", sm: "0.95rem" },
                          fontWeight: 700,
                          color: "#4A3428",
                          lineHeight: 1.2,
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {recipe.productTitle}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: { xs: "0.78rem", sm: "0.82rem" },
                          color: QUIZ.color.brown,
                          opacity: 0.8,
                          mt: "1px",
                        }}
                      >
                        {recipe.variantTitle} • £{recipe.price.toFixed(2)}
                      </Typography>
                    </Box>

                    {/* Increment */}
                    <IconButton
                      onClick={() => increment(recipe.variantId)}
                      sx={{
                        width: { xs: 30, sm: 34 },
                        height: { xs: 30, sm: 34 },
                        borderRadius: "8px",
                        background: QUIZ.color.brown,
                        "&:hover": { background: "#E96A67" },
                      }}
                    >
                      <AddRoundedIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: QUIZ.color.white }} />
                    </IconButton>
                  </Box>

                  {/* Count Display */}
                  <Typography
                    sx={{
                      fontSize: { xs: "1.1rem", sm: "1.25rem" },
                      fontWeight: 600,
                      color: QUIZ.color.brown,
                      textAlign: "center",
                      backgroundColor: "#FFF4ED",
                      borderRadius: "8px",
                      py: 0.5,
                      mt: 1,
                    }}
                  >
                    {count} {count === 1 ? "pot" : "pots"}
                  </Typography>
                </Box>
              );
            })
          )}
        </Stack>
      </Box>

      {/* Sticky Footer */}
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          borderTop: "1px solid rgba(0,0,0,.08)",
          background: QUIZ.color.cream,
          flexShrink: 0,
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <QuizButton variant="secondary" onClick={onClose} fullWidth sx={{ flex: 1 }}>
            Cancel
          </QuizButton>
          <QuizButton
            variant="primary"
            onClick={handleAddToBasket}
            disabled={!hasChanges}
            startIcon={<ShoppingBasketIcon />}
            fullWidth
            sx={{ flex: 1 }}
          >
            {hasChanges ? `Add to Basket (${totalAdded})` : "No Changes"}
          </QuizButton>
        </Stack>
      </Box>
    </Dialog>
  );
}
